import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/**
 * Bare `vendorApi` — owns the reducerPath, shared base query, and the
 * central tag-type taxonomy. Per-resource files (`./vendors`,
 * `./facilities`, `./vendorProducts`, `./merchantVendorProducts`,
 * `./config`) call `vendorApi.injectEndpoints` to attach their
 * endpoints. RTK Query mutates the shared definition in place at
 * injection time, so `vendorApi.endpoints[name]` becomes available
 * once the injecting file is imported.
 *
 * The small VAS lookup lives here as well — it doesn't fit any
 * per-resource bucket and isn't worth its own file. Keep this file
 * under ~150 LOC; if it grows, move VAS into a `./lookups.ts`
 * follow-up.
 */

/** Facility contact (typically a person at the facility — phone/email). */
export interface FacilityContact {
  name?: string;
  role?: string;
  phone?: string;
  email?: string;
}

export interface FacilityAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  countryCode?: string;
}

/** One Value-Added Service definition (from /v1/vas). */
export interface ValueAddedService {
  id: string;
  type: string;
  description?: string;
}

export const vendorApi = createApi({
  reducerPath: 'vendorApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Facility', 'VendorList', 'Vendor', 'VendorConfig', 'VendorPublicProfile'],
  endpoints: (builder) => ({
    /** GET /vendor/v1/vas — catalog of value-added services. */
    getValueAddedServices: builder.query<ValueAddedService[], void>({
      query: () => ({
        url: `${services.vendor}/v1/vas`,
        method: 'GET',
      }),
      transformResponse: (response: ValueAddedService[] | { vas?: ValueAddedService[] }) => {
        if (Array.isArray(response)) return response;
        return (response as { vas?: ValueAddedService[] }).vas ?? [];
      },
    }),
  }),
});

export const { useGetValueAddedServicesQuery } = vendorApi;
